import React from 'react';
import { TextField, Typography, Paper, Box } from '@mui/material';

import { convertToHalfWidthKatakana } from '../../utils/validations/common-validation';
import { useKatakanaConversion } from '../../hooks/use-katakana-conversion';
import { CustomerRegistrationForm } from '../../types/company.types';
import { PLACEHOLDER_MESSAGES } from '../../utils/constants/placeholder-messages';

import { getCustomerCategoryOptions } from '@/utils/mapping';
import { EnumSelect } from '@/components/select/enum-select';
import { getStylesForEditableField } from '@/utils/helpers';

interface CompanyInfoSectionProps {
  formData: CustomerRegistrationForm;
  originalFormData: CustomerRegistrationForm;
  isEditMode: boolean;
  onUpdateField: <K extends keyof CustomerRegistrationForm>(
    field: K,
    value: CustomerRegistrationForm[K]
  ) => void;
  getFieldError: (fieldName: string) => string | undefined;
  isFieldValid: (fieldName: string) => boolean;
}

export const CompanyInfoSection: React.FC<CompanyInfoSectionProps> = ({
  formData,
  originalFormData,
  isEditMode,
  onUpdateField,
  getFieldError,
  isFieldValid,
}) => {
  // Use katakana conversion hook
  const { handleBlur: handleKatakanaBlur } = useKatakanaConversion(
    formData.customerNameKatakana || '',
    (newValue) => onUpdateField('customerNameKatakana', newValue),
    'customerNameKatakana'
  );
  return (
    <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
      <Typography
        variant='h5'
        sx={{
          mb: 3,
          color: '#1A1C20',
          fontSize: '22px',
          fontWeight: 600,
        }}
      >
        得意先基本情報
      </Typography>

      {/* Required field notice */}
      <Typography
        sx={{
          mt: 1,
          mb: 2,
          color: '#717171',
          fontSize: '14px',
          fontWeight: 500,
        }}
      >
        * は必須項目です。
      </Typography>

      {/* Gali Customer Code */}
      <TextField
        fullWidth
        label='Gali取引先コード'
        placeholder={PLACEHOLDER_MESSAGES.GALI_CODE_INPUT}
        value={formData.galiCustomerCode || ''}
        onChange={(e) => onUpdateField('galiCustomerCode', e.target.value)}
        error={!isFieldValid('galiCustomerCode')}
        helperText={getFieldError('galiCustomerCode')}
        sx={{ mb: 3 }}
        slotProps={{
          input: {
            inputProps: { maxLength: 9, pattern: '[0-9]*', inputMode: 'numeric' },
            sx: getStylesForEditableField(
              !!isEditMode,
              originalFormData?.galiCustomerCode,
              formData.galiCustomerCode
            ),
          },
        }}
      />

      {/* Official Name */}
      <TextField
        fullWidth
        required
        label='正式名称'
        placeholder={PLACEHOLDER_MESSAGES.OFFICIAL_NAME_INPUT}
        value={formData.officialName}
        onChange={(e) => onUpdateField('officialName', e.target.value)}
        error={!isFieldValid('officialName')}
        helperText={getFieldError('officialName')}
        sx={{ mb: 3 }}
        slotProps={{
          input: {
            inputProps: { maxLength: 200 },
            sx: getStylesForEditableField(
              !!isEditMode,
              originalFormData?.officialName,
              formData.officialName
            ),
          },
        }}
      />

      {/* Abbreviated Name */}
      <TextField
        fullWidth
        required
        label='略称（簡易名称）'
        placeholder={PLACEHOLDER_MESSAGES.ABBREVIATED_NAME_INPUT}
        value={formData.abbreviatedName}
        onChange={(e) => onUpdateField('abbreviatedName', e.target.value)}
        error={!isFieldValid('abbreviatedName')}
        helperText={getFieldError('abbreviatedName')}
        sx={{ mb: 3 }}
        slotProps={{
          input: {
            inputProps: { maxLength: 100 },
            sx: getStylesForEditableField(
              !!isEditMode,
              originalFormData?.abbreviatedName,
              formData.abbreviatedName
            ),
          },
        }}
      />

      {/* Branch/Department */}
      <TextField
        fullWidth
        label='支店・部門'
        placeholder={PLACEHOLDER_MESSAGES.BRANCH_DEPARTMENT_INPUT}
        value={formData.branchDepartment || ''}
        onChange={(e) => onUpdateField('branchDepartment', e.target.value)}
        error={!isFieldValid('branchDepartment')}
        helperText={getFieldError('branchDepartment')}
        sx={{ mb: 3 }}
        slotProps={{
          input: {
            inputProps: { maxLength: 100 },
            sx: getStylesForEditableField(
              !!isEditMode,
              originalFormData?.branchDepartment,
              formData.branchDepartment
            ),
          },
        }}
      />

      {/* Customer Name in Katakana */}
      <TextField
        fullWidth
        required
        label='取引先名カナ (半角ｶﾅ)'
        placeholder={PLACEHOLDER_MESSAGES.COMPANY_NAME_KATAKANA_INPUT}
        value={formData.customerNameKatakana || ''}
        onChange={(e) => {
          const convertedValue = convertToHalfWidthKatakana(e.target.value);
          onUpdateField('customerNameKatakana', convertedValue);
        }}
        onBlur={handleKatakanaBlur}
        error={!isFieldValid('customerNameKatakana')}
        helperText={getFieldError('customerNameKatakana')}
        sx={{ mb: 3 }}
        slotProps={{
          input: {
            inputProps: { maxLength: 200, style: { fontFamily: 'monospace' } },
            sx: getStylesForEditableField(
              !!isEditMode,
              originalFormData?.customerNameKatakana,
              formData.customerNameKatakana
            ),
          },
        }}
      />

      {/* Customer Category (only for 得意先) */}
      {formData.companyType === '得意先' && (
        <Box sx={{ mb: 3 }}>
          <EnumSelect
            value={formData.customerCategory || ''}
            onChange={(value) => onUpdateField('customerCategory', value)}
            label='得意先カテゴリ'
            options={getCustomerCategoryOptions()}
            required
            error={!isFieldValid('customerCategory')}
            slotProps={{
              input: {
                sx: getStylesForEditableField(
                  !!isEditMode,
                  originalFormData?.customerCategory,
                  formData.customerCategory
                ),
              },
            }}
          />
          {getFieldError('customerCategory') && (
            <Typography variant='caption' color='error' sx={{ mt: 1, ml: 2 }}>
              {getFieldError('customerCategory')}
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
};
